import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChevronLeft, ChevronRight, Plus, Clock, User, Pencil } from 'lucide-react';
interface Event {
  id: number;
  title: string;
  date: string;
  time: string;
  contact: string;
  type: 'meeting' | 'call' | 'followup' | 'deadline';
}
interface WeekStats {
  id: number;
  kw: number;
  calls: number;
  angebote: number;
  verkaufe: number;
  umsatzCash: number;
  cashPercent: number;
}
export function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<'month' | 'week' | 'day'>('month');
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    time: '',
    contact: '',
    type: 'meeting' as Event['type']
  });
  const [events, setEvents] = useState<Event[]>([{
    id: 1,
    title: 'Demo Call mit Thomas Weber',
    date: '2025-01-28',
    time: '10:00',
    contact: 'Thomas Weber',
    type: 'call'
  }, {
    id: 2,
    title: 'Follow-up Max Mustermann',
    date: '2025-01-28',
    time: '14:00',
    contact: 'Max Mustermann',
    type: 'followup'
  }, {
    id: 3,
    title: 'Meeting Anna Schmidt',
    date: '2025-01-29',
    time: '11:00',
    contact: 'Anna Schmidt',
    type: 'meeting'
  }, {
    id: 4,
    title: 'Angebot Deadline',
    date: '2025-01-30',
    time: '17:00',
    contact: 'Michael Becker',
    type: 'deadline'
  }, {
    id: 5,
    title: 'Termin Julia Fischer',
    date: '2025-01-31',
    time: '15:00',
    contact: 'Julia Fischer',
    type: 'meeting'
  }]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [weekStats, setWeekStats] = useState<WeekStats[]>([{
    id: 1,
    kw: 1,
    calls: 45,
    angebote: 12,
    verkaufe: 8,
    umsatzCash: 24000,
    cashPercent: 75
  }, {
    id: 2,
    kw: 2,
    calls: 52,
    angebote: 15,
    verkaufe: 10,
    umsatzCash: 31000,
    cashPercent: 82
  }, {
    id: 3,
    kw: 3,
    calls: 38,
    angebote: 10,
    verkaufe: 6,
    umsatzCash: 18500,
    cashPercent: 68
  }]);
  const [editingCell, setEditingCell] = useState<{
    id: number;
    field: keyof WeekStats;
  } | null>(null);
  const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return {
      daysInMonth,
      startingDayOfWeek
    };
  };
  const getEventsForDate = (date: string) => {
    return events.filter(e => e.date === date);
  };
  const formatDate = (day: number) => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${month}-${dayStr}`;
  };
  const handleDayClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    setNewEvent({
      ...newEvent,
      date: dateStr
    });
    setShowAddEvent(true);
  };
  const handleSaveEvent = () => {
    if (newEvent.title && newEvent.date && newEvent.time && newEvent.contact) {
      const event: Event = {
        id: events.length + 1,
        ...newEvent
      };
      setEvents([...events, event]);
      setNewEvent({
        title: '',
        date: '',
        time: '',
        contact: '',
        type: 'meeting'
      });
      setShowAddEvent(false);
      setSelectedDate('');
    }
  };
  const handleCancelEvent = () => {
    setShowAddEvent(false);
    setSelectedDate('');
    setNewEvent({
      title: '',
      date: '',
      time: '',
      contact: '',
      type: 'meeting'
    });
  };
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };
  const typeColors: Record<string, string> = {
    meeting: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30',
    call: 'bg-orange-400/10 text-orange-500 dark:text-orange-300 border-orange-400/30',
    followup: 'bg-[#f88d3e]/10 text-[#f88d3e] dark:text-[#f88d3e] border-[#f88d3e]/30',
    deadline: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30'
  };
  const {
    daysInMonth,
    startingDayOfWeek
  } = getDaysInMonth(currentDate);
  const days = Array.from({
    length: daysInMonth
  }, (_, i) => i + 1);
  const emptyDays = Array.from({
    length: startingDayOfWeek
  }, (_, i) => i);
  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date()).sort((a, b) => new Date(a.date + ' ' + a.time).getTime() - new Date(b.date + ' ' + b.time).getTime()).slice(0, 5);
  const handleAddWeek = () => {
    const maxKw = Math.max(...weekStats.map(s => s.kw), 0);
    const newWeek: WeekStats = {
      id: weekStats.length + 1,
      kw: maxKw + 1,
      calls: 0,
      angebote: 0,
      verkaufe: 0,
      umsatzCash: 0,
      cashPercent: 0
    };
    setWeekStats([...weekStats, newWeek]);
  };
  const handleStatChange = (id: number, field: keyof WeekStats, value: string) => {
    const numValue = parseFloat(value) || 0;
    setWeekStats(prev => prev.map(stat => stat.id === id ? {
      ...stat,
      [field]: numValue
    } : stat));
  };
  const calculateVerkaufePercent = (verkaufe: number, angebote: number) => {
    if (angebote === 0) return 0;
    return Math.round(verkaufe / angebote * 100);
  };
  const years = Array.from({
    length: 5
  }, (_, i) => new Date().getFullYear() - 2 + i);
  return <div className="p-6 space-y-6" data-id="element-170">
      <div className="flex items-center justify-between" data-id="element-171">
        <div data-id="element-172">
          <h1 className="text-3xl font-bold text-foreground" data-id="element-173">Kalender</h1>
          <p className="text-muted-foreground mt-1" data-id="element-174">
            Termine, Follow-ups und Deadlines verwalten
          </p>
        </div>
        <div className="flex gap-2" data-id="element-175">
          <Button variant="outline" size="sm" onClick={() => setView('day')} data-id="element-176">
            Tag
          </Button>
          <Button variant="outline" size="sm" onClick={() => setView('week')} data-id="element-177">
            Woche
          </Button>
          <Button variant={view === 'month' ? 'default' : 'outline'} size="sm" onClick={() => setView('month')} data-id="element-178">
            Monat
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setShowAddEvent(!showAddEvent)} data-id="element-179">
            <Plus className="h-4 w-4 mr-1" data-id="element-180" />
            Termin
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-181">
        <div className="lg:col-span-2" data-id="element-182">
          <Card data-id="element-183">
            <CardHeader data-id="element-184">
              <div className="flex items-center justify-between" data-id="element-185">
                <CardTitle className="text-lg" data-id="element-186">
                  {monthNames[currentDate.getMonth()]}{' '}
                  {currentDate.getFullYear()}
                </CardTitle>
                <div className="flex gap-2" data-id="element-187">
                  <Button variant="outline" size="icon" onClick={previousMonth} data-id="element-188">
                    <ChevronLeft className="h-4 w-4" data-id="element-189" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={nextMonth} data-id="element-190">
                    <ChevronRight className="h-4 w-4" data-id="element-191" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-192">
              <div className="grid grid-cols-7 gap-2" data-id="element-193">
                {dayNames.map(day => <div key={day} className="text-center text-xs font-medium text-muted-foreground p-2" data-id="element-194">
                    {day}
                  </div>)}
                {emptyDays.map(i => <div key={`empty-${i}`} className="p-2" data-id="element-195"></div>)}
                {days.map(day => {
                const dateStr = formatDate(day);
                const dayEvents = getEventsForDate(dateStr);
                const isToday = dateStr === new Date().toISOString().split('T')[0];
                const isSelected = dateStr === selectedDate;
                return <div key={day} onClick={() => handleDayClick(dateStr)} className={`min-h-[80px] p-2 border rounded-lg hover:bg-[#ededed]/20 dark:hover:bg-white/5 transition-colors cursor-pointer ${isToday ? 'border-[#f88d3e] bg-[#ededed]/20 dark:bg-[#f88d3e]/10' : isSelected ? 'border-[#f88d3e] dark:border-[#f88d3e]' : 'border-border'}`} data-id="element-196">
                      <div className={`text-sm font-medium mb-1 ${isToday ? 'text-[#f88d3e] dark:text-[#f88d3e]' : 'text-foreground'}`} data-id="element-197">
                        {day}
                      </div>
                      <div className="space-y-1" data-id="element-198">
                        {dayEvents.slice(0, 2).map(event => <div key={event.id} className={`text-xs px-1 py-0.5 rounded border truncate ${typeColors[event.type]}`} data-id="element-199">
                            {event.time} {event.title}
                          </div>)}
                        {dayEvents.length > 2 && <div className="text-xs text-muted-foreground" data-id="element-200">
                            +{dayEvents.length - 2} mehr
                          </div>}
                      </div>
                    </div>;
              })}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6" data-id="element-201">
          {showAddEvent && <Card data-id="element-202">
              <CardHeader data-id="element-203">
                <CardTitle className="text-base" data-id="element-204">Neuer Termin</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-205">
                <div className="space-y-2" data-id="element-206">
                  <Label data-id="element-207">Titel</Label>
                  <Input placeholder="z.B. Meeting mit Kunde" value={newEvent.title} onChange={e => setNewEvent({
                ...newEvent,
                title: e.target.value
              })} data-id="element-208" />
                </div>
                <div className="space-y-2" data-id="element-209">
                  <Label data-id="element-210">Datum</Label>
                  <Input type="date" value={newEvent.date} onChange={e => setNewEvent({
                ...newEvent,
                date: e.target.value
              })} data-id="element-211" />
                </div>
                <div className="space-y-2" data-id="element-212">
                  <Label data-id="element-213">Uhrzeit</Label>
                  <Input type="time" value={newEvent.time} onChange={e => setNewEvent({
                ...newEvent,
                time: e.target.value
              })} data-id="element-214" />
                </div>
                <div className="space-y-2" data-id="element-215">
                  <Label data-id="element-216">Kontakt</Label>
                  <Input placeholder="Name des Kontakts" value={newEvent.contact} onChange={e => setNewEvent({
                ...newEvent,
                contact: e.target.value
              })} data-id="element-217" />
                </div>
                <div className="space-y-2" data-id="element-218">
                  <Label data-id="element-219">Typ</Label>
                  <select className="w-full h-10 px-3 rounded-lg border border-input bg-background text-foreground text-sm" value={newEvent.type} onChange={e => setNewEvent({
                ...newEvent,
                type: e.target.value as Event['type']
              })} data-id="element-220">
                    <option value="meeting" data-id="element-221">Meeting</option>
                    <option value="call" data-id="element-222">Anruf</option>
                    <option value="followup" data-id="element-223">Follow-up</option>
                    <option value="deadline" data-id="element-224">Deadline</option>
                  </select>
                </div>
                <div className="flex gap-2" data-id="element-225">
                  <Button variant="outline" className="flex-1" onClick={handleCancelEvent} data-id="element-226">
                    Abbrechen
                  </Button>
                  <Button variant="secondary" className="flex-1" onClick={handleSaveEvent} data-id="element-227">
                    Speichern
                  </Button>
                </div>
              </CardContent>
            </Card>}

          <Card data-id="element-228">
            <CardHeader data-id="element-229">
              <CardTitle className="text-base" data-id="element-230">Anstehende Termine</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3" data-id="element-231">
              {upcomingEvents.map(event => <div key={event.id} className="p-3 border border-border rounded-lg hover:bg-[#ededed]/20 dark:hover:bg-white/5 transition-colors cursor-pointer" data-id="element-232">
                  <div className="flex items-start gap-3" data-id="element-233">
                    <div className={`px-2 py-1 rounded text-xs border ${typeColors[event.type]}`} data-id="element-234">
                      {event.type === 'meeting' && 'Meeting'}
                      {event.type === 'call' && 'Call'}
                      {event.type === 'followup' && 'Follow-up'}
                      {event.type === 'deadline' && 'Deadline'}
                    </div>
                    <div className="flex-1" data-id="element-235">
                      <p className="font-medium text-sm text-foreground" data-id="element-236">
                        {event.title}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground" data-id="element-237">
                        <span className="flex items-center gap-1" data-id="element-238">
                          <Clock className="h-3 w-3" data-id="element-239" />
                          {event.date} {event.time}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-240">
                          <User className="h-3 w-3" data-id="element-241" />
                          {event.contact}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>)}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Statistics Section */}
      <Card data-id="element-242">
        <CardHeader data-id="element-243">
          <div className="flex items-center justify-between" data-id="element-244">
            <div data-id="element-245">
              <CardTitle className="text-lg" data-id="element-246">Wochenstatistik</CardTitle>
              <p className="text-sm text-muted-foreground mt-1" data-id="element-247">
                KPI-Tracking pro Kalenderwoche
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-248">
              <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} className="h-9 px-3 rounded-md border border-input bg-background text-sm" data-id="element-249">
                {years.map(year => <option key={year} value={year} data-id="element-250">
                    {year}
                  </option>)}
              </select>
              <Button variant="secondary" size="sm" onClick={handleAddWeek} data-id="element-251">
                <Plus className="h-4 w-4 mr-1" data-id="element-252" />
                Neue KW
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent data-id="element-253">
          <div className="overflow-x-auto" data-id="element-254">
            <table className="w-full" data-id="element-255">
              <thead className="bg-[#ededed]/30 dark:bg-white/5" data-id="element-256">
                <tr data-id="element-257">
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3" data-id="element-258">
                    KW
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3" data-id="element-259">
                    Calls
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3" data-id="element-260">
                    Angebote
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3" data-id="element-261">
                    Verkäufe
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3" data-id="element-262">
                    % Verkäufe
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3" data-id="element-263">
                    Umsatz Cash
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3" data-id="element-264">
                    Cash %
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border" data-id="element-265">
                {weekStats.map(stat => <tr key={stat.id} className="hover:bg-[#ededed]/20 dark:hover:bg-white/5 transition-colors" data-id="element-266">
                    <td className="px-4 py-3" data-id="element-267">
                      <Input type="number" value={stat.kw} onChange={e => handleStatChange(stat.id, 'kw', e.target.value)} className="h-8 w-16 text-sm" data-id="element-268" />
                    </td>
                    <td className="px-4 py-3" data-id="element-269">
                      <Input type="number" value={stat.calls} onChange={e => handleStatChange(stat.id, 'calls', e.target.value)} className="h-8 w-20 text-sm" data-id="element-270" />
                    </td>
                    <td className="px-4 py-3" data-id="element-271">
                      <Input type="number" value={stat.angebote} onChange={e => handleStatChange(stat.id, 'angebote', e.target.value)} className="h-8 w-20 text-sm" data-id="element-272" />
                    </td>
                    <td className="px-4 py-3" data-id="element-273">
                      <Input type="number" value={stat.verkaufe} onChange={e => handleStatChange(stat.id, 'verkaufe', e.target.value)} className="h-8 w-20 text-sm" data-id="element-274" />
                    </td>
                    <td className="px-4 py-3" data-id="element-275">
                      <div className="text-sm font-medium text-foreground" data-id="element-276">
                        {calculateVerkaufePercent(stat.verkaufe, stat.angebote)}
                        %
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-277">
                      <Input type="number" value={stat.umsatzCash} onChange={e => handleStatChange(stat.id, 'umsatzCash', e.target.value)} className="h-8 w-24 text-sm" data-id="element-278" />
                    </td>
                    <td className="px-4 py-3" data-id="element-279">
                      <Input type="number" value={stat.cashPercent} onChange={e => handleStatChange(stat.id, 'cashPercent', e.target.value)} className="h-8 w-20 text-sm" data-id="element-280" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>;
}